import React from "react";

/**
 * ChapterRow — strict 3-column editorial spread.
 *
 *   NARRATIVE (col-3)   |   ILLUSTRATION (col-7)   |   INDEX (col-2)
 *
 * Optional `epigraph` renders an italic editorial preamble above the
 * chapter label — used to pose a manuscript question that frames the
 * chapter (e.g. "Who are the Ramirez? — A family that helped shape
 * the tobacco industry.").
 *
 * Optional `bridge` renders a quiet bottom-of-chapter editorial bridge
 * pointing to the next chapter — closes the emotional arc.
 */
export default function ChapterRow({
  id,
  testid,
  numeral,
  name,
  chapterLabel,
  epigraph,
  headline,
  body,
  cta,
  illustration,
  meta,
  bridge,
  bg = "bg-pearl",
  divider = true,
  className = "",
}) {
  return (
    <section
      id={id}
      data-testid={testid}
      className={`relative ${bg} text-tobacco py-16 md:py-20 ${
        divider ? "border-t border-tobacco/10" : ""
      } ${className}`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-10">
        {/* LEFT — narrative column */}
        <div className="col-span-12 md:col-span-3 flex flex-col">
          {epigraph ? (
            <div className="mb-6 md:mb-7 font-display italic text-tobacco/65 text-[16px] md:text-[17px] leading-snug max-w-[28ch]">
              {epigraph}
            </div>
          ) : null}
          {chapterLabel ? (
            <div className="font-mono text-[10px] md:text-[10.5px] tracking-widest2 uppercase text-tobacco/65 mb-5">
              {chapterLabel}
            </div>
          ) : null}
          {headline ? (
            <h2 className="font-display italic text-tobacco leading-[1.04] text-[34px] md:text-[36px] lg:text-[42px] font-normal max-w-[14ch]">
              {headline}
            </h2>
          ) : null}
          {body ? (
            <div className="mt-5 space-y-3 text-tobacco/80 text-[15px] md:text-[14.5px] leading-relaxed max-w-[36ch]">
              {typeof body === "string" ? <p>{body}</p> : body}
            </div>
          ) : null}
          {cta ? <div className="mt-6">{cta}</div> : null}
        </div>

        {/* CENTER — illustration canvas */}
        <div className="col-span-12 md:col-span-7 flex items-center justify-center mt-4 md:mt-0">
          {illustration}
        </div>

        {/* RIGHT — manuscript notes + chapter marker */}
        <aside className="col-span-12 md:col-span-2 flex md:flex-col items-start md:items-end justify-between md:justify-start gap-6 md:gap-8 md:pl-4 mt-4 md:mt-0">
          {meta ? <div className="md:order-1 md:mb-6 md:text-right w-full">{meta}</div> : null}
          <div className="md:order-2 md:mt-auto md:text-right">
            <div className="font-mono text-[11px] tracking-widest2 text-tobacco/85">
              {numeral}
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-widest2 uppercase text-gold/90">
              {name}
            </div>
          </div>
        </aside>
      </div>

      {/* Closing bridge — quiet editorial transition into the next chapter */}
      {bridge ? (
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 mt-12 md:mt-16">
          <div className="border-t border-tobacco/15 pt-5 flex flex-wrap items-baseline justify-between gap-3 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
            {bridge}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* Small helper — narrative CTA link with arrow tick */
export function ChapterCta({ children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-3 font-mono text-[10.5px] tracking-widest2 uppercase text-gold hover:opacity-70 transition-opacity duration-700"
    >
      {children}
      <span className="arrow-tick" />
    </button>
  );
}

/* Manuscript Note — archival data strip rendered in the right column. */
export function ManuscriptNote({ items = [], label }) {
  return (
    <div className="text-left md:text-right">
      {label ? (
        <div className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 mb-3">
          {label}
        </div>
      ) : null}
      <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-3.5">
        {items.map((it, i) => (
          <div key={i} className="md:border-t md:border-tobacco/15 md:pt-3">
            <dt className="font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/50">
              {it.k}
            </dt>
            <dd className="mt-1 font-display italic text-tobacco/90 text-[15px] md:text-[16px] leading-tight">
              {it.v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* Bridge — emotional transition between chapters. */
export function ChapterBridge({ left, right, onClick, testid }) {
  return (
    <>
      <span className="italic font-display text-tobacco/70 text-[14px] md:text-[15px] normal-case tracking-normal">
        {left}
      </span>
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className="inline-flex items-center gap-3 text-gold/85 hover:text-gold transition-colors duration-500"
      >
        {right}
        <span className="arrow-tick" />
      </button>
    </>
  );
}
